import React, { useEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { Modal, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styled, { css } from 'styled-components';
import { API_BASE, TOAST_CONFIG } from '../../shared/Constants';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';

export interface HelpFormProps {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
}

export default function HelpForm({ openModal, setOpenModal }: HelpFormProps) {
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const { user, getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    if (openModal) {
      const displayedName = user?.given_name && user?.family_name
        ? `${user.given_name} ${user.family_name}`
        : user?.name ?? '';
      setName(displayedName || "");
      setEmail(user?.email ?? "");
      setMessage("");
    }
  }, [user, openModal]);

  const handleClose = () => setOpenModal(false);

  const sendHelpRequest = async () => {
    if (isSubmitting) return;
    const nameVal = name.trim();
    const emailVal = email.trim();
    const messageVal = message.trim();

    if (!nameVal || !emailVal || !messageVal) {
      toast.error('Preencha todos os campos', TOAST_CONFIG);
      return;
    }

    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(emailVal)) {
      toast.error('Email inválido', TOAST_CONFIG);
      return;
    }

    try {
      setIsSubmitting(true);
      const token = await getAccessTokenSilently();
      const url = `${API_BASE}/api/help`;
      const body = {
        name: nameVal,
        contact_info: emailVal,
        date: new Date().toISOString(),
        subject: 'CHAT',
        message: messageVal,
      };
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Auth ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        toast.success('Pedido de ajuda enviado com sucesso', TOAST_CONFIG);
        handleClose();
      } else {
        toast.error('Erro ao enviar pedido de ajuda', TOAST_CONFIG);
      }
    } catch {
      toast.error('Ocorreu um erro no formulário de ajuda.', TOAST_CONFIG);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={openModal} onClose={handleClose}>
      <Backdrop onClick={handleClose}>
        <Container role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
          <Header>
            <HeaderTop>
              <Title>Central de Suporte</Title>
              <IconButton 
                aria-label="Fechar" 
                onClick={handleClose} 
                size="small"
                sx={{
                  color: (theme) => theme.palette.mode === 'dark' ? '#9ca3af' : 'inherit',
                  '&:hover': {
                    color: (theme) => theme.palette.mode === 'dark' ? '#e5e7eb' : 'inherit',
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </HeaderTop>
            <Subtitle>
              Preencha este formulário para que possamos entrar em contato através do email informado.
            </Subtitle>
          </Header>

          <FormContainer>
            <FormGrid>
              <FormField>
                <Label>Nome Completo *</Label>
                <StyledInput
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  type="text"
                  required
                  maxLength={70}
                  placeholder="Seu nome completo"
                />
              </FormField>

              <FormField>
                <Label>E-mail *</Label>
                <StyledInput
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  required
                  maxLength={100}
                  placeholder="seu@email.com"
                />
              </FormField>

              <FormFieldFullWidth>
                <Label>Mensagem *</Label>
                <StyledTextarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  maxLength={1000}
                  placeholder="Descreva seu problema ou dúvida detalhadamente..."
                />
              </FormFieldFullWidth>
            </FormGrid>
          </FormContainer>

          <Footer>
            <SendButton type="button" onClick={sendHelpRequest} disabled={isSubmitting} aria-busy={isSubmitting}>
              {isSubmitting && <CircularProgress size={18} sx={{ color: '#ffffff', mr: 1 }} />}
              {isSubmitting ? 'Enviando...' : 'Enviar Solicitação'}
            </SendButton>
          </Footer>
        </Container>
      </Backdrop>
    </Modal>
  );
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: ${(props) => props.theme.mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.45)'};
  z-index: 1300;
  backdrop-filter: blur(4px);
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: min(640px, 100%);
  max-height: 90vh;
  overflow-y: auto;
  background: ${(props) => props.theme.payment_modal_background};
  color: ${(props) => props.theme.payment_modal_text};
  border-radius: 16px;
  border: 1px solid ${(props) => props.theme.payment_modal_border};
  padding: 28px 28px 32px 28px;
  box-shadow: ${(props) => props.theme.mode === 'dark' 
    ? '0 20px 45px rgba(0, 0, 0, 0.5)' 
    : '0 20px 45px rgba(15, 23, 42, 0.18)'};
  font-family: 'Sora', sans-serif;

  /* Scrollbar customization */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.payment_modal_scrollbar};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${(props) => props.theme.payment_modal_scrollbar_hover};
  }

  @media (max-width: 480px) {
    padding: 20px;
    border-radius: 12px;
  }
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.45rem;
  font-weight: 600;
  color: ${(props) => props.theme.payment_modal_text};
  font-family: 'Sora', sans-serif;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${(props) => props.theme.payment_modal_text_secondary};
  font-family: 'Sora', sans-serif;
`;

const FormContainer = styled.div`
  margin-bottom: 24px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FormFieldFullWidth = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  grid-column: 1 / -1;
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(props) => props.theme.payment_modal_text_secondary};
  font-family: 'Sora', sans-serif;
`;

const sharedInputStyles = css`
  font-family: 'Sora', sans-serif;
  padding: 12px 14px;
  font-size: 0.95rem;
  border: 1px solid ${(props) => props.theme.payment_modal_border};
  border-radius: 8px;
  background-color: ${(props) => props.theme.mode === 'dark' ? props.theme.input_surface_bg : props.theme.payment_modal_background};
  color: ${(props) => props.theme.payment_modal_text};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: ${(props) => props.theme.payment_modal_text_secondary};
    opacity: ${(props) => props.theme.mode === 'dark' ? '0.6' : '0.8'};
  }

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.mode === 'dark' ? props.theme.blue_color : props.theme.payment_modal_text_secondary};
    box-shadow: ${(props) => props.theme.mode === 'dark' 
      ? '0 0 0 3px rgba(138, 180, 248, 0.15)' 
      : '0 0 0 3px rgba(26, 60, 52, 0.12)'};
  }

  &:hover:not(:focus) {
    border-color: ${(props) => props.theme.mode === 'dark' ? '#555' : props.theme.payment_modal_border};
  }
`;

const StyledInput = styled.input`
  ${sharedInputStyles}
`;

const StyledTextarea = styled.textarea`
  ${sharedInputStyles}
  resize: vertical;
  min-height: 140px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-start;
  padding-top: 20px;
  border-top: 1px solid ${(props) => props.theme.payment_modal_border};
`;

const SendButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 200px;
  padding: 12px 24px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-family: 'Sora', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  background-color: ${(props) => props.theme.mode === 'dark' ? props.theme.blue_color : '#1A3C34'};
  color: ${(props) => props.theme.mode === 'dark' ? '#0b1120' : '#ffffff'};
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${(props) => props.theme.mode === 'dark' ? props.theme.blue_color_hover : '#14352C'};
  }

  &:active:not(:disabled) {
    background-color: ${(props) => props.theme.mode === 'dark' ? '#5a8fd9' : '#102820'};
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
